"use client";

import { useEffect, useRef } from "react";
import Section, { SectionHeader } from "@/components/Section";
import DashboardPreview from "@/components/DashboardPreview";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default function DashboardSection() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.registerPlugin(ScrollTrigger);
    if (!containerRef.current) return;

    const formatValue = (n: number, format: string | null) => {
      if (format === "percent") return `${n.toFixed(1)}%`;
      return Math.round(n).toLocaleString("en-US");
    };

    const ctx = gsap.context(() => {
      const trigger = {
        trigger: containerRef.current,
        start: "top 75%",
        once: true,
      } as const;

      gsap.from(".anim-dash-text", {
        scrollTrigger: trigger,
        x: -50,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".anim-dash-ui", {
        scrollTrigger: trigger,
        x: 50,
        duration: 0.8,
        delay: 0.1,
        ease: "power3.out",
      });

      gsap.from(".dash-stat-card", {
        scrollTrigger: trigger,
        y: 16,
        duration: 0.6,
        stagger: 0.1,
        delay: 0.35,
        ease: "power3.out",
      });

      gsap.to(".dash-chart-line", {
        scrollTrigger: trigger,
        strokeDashoffset: 0,
        duration: 1.4,
        delay: 0.55,
        ease: "power2.out",
      });

      gsap.to(".dash-chart-clip", {
        scrollTrigger: trigger,
        attr: { width: 320 },
        duration: 1.4,
        delay: 0.55,
        ease: "power2.out",
      });

      const valueEls = containerRef.current?.querySelectorAll<HTMLElement>(".dash-stat-value");
      valueEls?.forEach((el) => {
        const target = parseFloat(el.dataset.countTarget ?? "0");
        const format = el.dataset.countFormat ?? "int";
        const proxy = { value: 0 };
        gsap.to(proxy, {
          scrollTrigger: trigger,
          value: target,
          duration: 1.4,
          delay: 0.4,
          ease: "power2.out",
          onUpdate: () => {
            el.textContent = formatValue(proxy.value, format);
          },
        });
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section id="dashboard" className="overflow-clip bg-[#121212]">
      <div ref={containerRef}>
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.3fr]">
          
          {/* Left Side: Text */}
          <div className="anim-dash-text flex flex-col items-start text-left lg:order-1 order-1">
            <SectionHeader
              eyebrow="The dashboard"
              title={<>A control room for your <span className="text-gradient-warm">referral program</span></>}
              description="Every knob your program needs in one place — campaigns, users, webhooks, analytics, and API keys. Project-scoped and securely isolated to your organization."
              align="left"
              className="!mx-0"
            />
          </div>

          {/* Right Side: Dashboard UI */}
          <div className="anim-dash-ui relative w-full lg:order-2 order-2 mt-10 lg:mt-0">
            <DashboardPreview />
          </div>
        </div>
      </div>
    </Section>
  );
}
